/**
 * Utility functions for Nova Test UI
 */

import { formatDistanceToNow } from 'date-fns';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { DiaperModel } from './types';

/**
 * Merge class names with Tailwind
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Calculate wetness volume using cubic polynomial (backend formula)
 */
export function calculateWetnessVolume(
  wetnessLevel: number,
  model: DiaperModel | null
): {
  volume: number | null;
  percent: number | null;
  model: DiaperModel | null;
} {
  if (!model) {
    return { volume: null, percent: null, model: null };
  }

  const W = wetnessLevel;
  // Polynomial: volume = c0 + c1*W + c2*W² + c3*W³
  const volume = model.c0 + model.c1 * W + model.c2 * Math.pow(W, 2) + model.c3 * Math.pow(W, 3);
  const clampedVolume = Math.max(0, Math.min(model.capacity, volume));
  const percent = (clampedVolume / model.capacity) * 100;

  return {
    volume: Math.round(clampedVolume),
    percent: Math.round(percent * 10) / 10,
    model,
  };
}

/**
 * Get wetness percent for a given wetness value and model
 */
export function getWetnessPercent(wetnessLevel: number, model: DiaperModel | null): number | null {
  const calc = calculateWetnessVolume(wetnessLevel, model);
  return calc && typeof calc.percent === 'number' ? calc.percent : null;
}

/**
 * Calculate the derivative (slope/rate of change) of the wetness polynomial at a specific wetness value
 * Returns mL per wetness unit, showing how sensitive the volume reading is at that point
 * 
 * Polynomial: volume = c0 + c1*W + c2*W² + c3*W³
 * Derivative: dV/dW = c1 + 2*c2*W + 3*c3*W²
 */
export function calculatePolynomialSlope(
  wetnessLevel: number,
  model: DiaperModel | null
): number | null {
  if (!model) {
    return null;
  }

  const W = wetnessLevel;
  // Derivative of cubic polynomial
  const slope = model.c1 + 2 * model.c2 * W + 3 * model.c3 * Math.pow(W, 2);
  
  return slope;
}

/**
 * Find wetness level for target percent using binary search
 * Returns a value in [0, 32767]. If target is unreachable, returns boundary.
 */
export function findWetnessForPercent(
  targetPercent: number,
  model: DiaperModel | null
): number {
  if (!model) return 0;

  let lo = 0;
  let hi = 32767;
  const maxPercent = getWetnessPercent(hi, model) ?? 0;
  
  if (maxPercent < targetPercent) return hi; // unreachable within slider range
  
  // Binary search for ~20 iterations
  for (let i = 0; i < 20; i++) {
    const mid = Math.floor((lo + hi) / 2);
    const p = getWetnessPercent(mid, model) ?? 0;
    if (p < targetPercent) lo = mid;
    else hi = mid;
  }
  
  return hi;
}

/**
 * Get relative time string (e.g., "10 seconds ago")
 */
export function getTimeAgo(timestamp: string | Date): string {
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return 'unknown time';
  }
}

/**
 * Generate unique MAC addresses
 */
export function generateMACAddresses(count: number = 150): string[] {
  const macs: string[] = [];
  
  for (let i = 1; i <= count; i++) {
    const mac = `AA:BB:CC:${String(Math.floor(i / 256))
      .padStart(2, '0')}:${String(i % 256).padStart(2, '0')}:${String((i * 7) % 256).padStart(2, '0')}`
      .replace(/(\d+)/g, (match) => parseInt(match).toString(16).toUpperCase().padStart(2, '0'));
    macs.push(mac);
  }
  
  return macs;
}

/**
 * Get status text from status code
 */
export function getStatusText(code: number): string {
  const statuses: Record<number, string> = {
    0: 'OK',
    1: 'Detached',
    2: 'Shorted',
    3: 'Same Wet Diaper Attached',
    4: 'Wet Diaper Attached',
    5: 'Leak Imminent',
    6: 'Identifying',
    7: 'Preattached (Clip Rebooted)',
    8: 'Recovered',
    9: 'Entering Sleep Mode',
    30: 'Pooling',
    255: 'Boot (0xFF)',
  };
  return statuses[code] || `Unknown (${code})`;
}

/**
 * Build 13-byte sensor packet payload
 */
export function buildSensorPacket(params: {
  wetness: number;
  statusCode?: number;
  battery?: number;
  diaperModel?: number;
  diaperCounter?: number;
  firmwareRevision?: number;
  adcValues?: number[];
}): number[] {
  const {
    wetness,
    statusCode = 0,
    battery = 80,
    diaperModel = 30,
    diaperCounter = 30,
    firmwareRevision = 34,
    adcValues = [3, 2, 1, 1, 0],
  } = params;

  const wetnessLSB = wetness & 0xff;
  const wetnessMSB = (wetness >> 8) & 0xff;
  const diaperModelLSB = diaperModel & 0xff;
  const diaperModelMSB = (diaperModel >> 8) & 0xff;

  return [
    firmwareRevision, // [0] Firmware version
    statusCode, // [1] Status code
    wetnessLSB, // [2] Wetness LSB
    wetnessMSB, // [3] Wetness MSB
    diaperModelLSB, // [4] Diaper model LSB
    diaperModelMSB, // [5] Diaper model MSB
    diaperCounter, // [6] Diaper counter
    adcValues[0], // [7] ADC C5
    adcValues[1], // [8] ADC C4
    adcValues[2], // [9] ADC C3
    adcValues[3], // [10] ADC C2
    adcValues[4], // [11] ADC C1
    battery, // [12] Battery percent
  ];
}

/**
 * Relay configuration (same as in Postman)
 */
export const RELAY_ADDRESS = 'D4:63:C6:61:D1:55';
export const RELAY_ANDROID_ID = '94dfe1db1f47e4e8';

/**
 * Event icon mapping (matching icon generator from backend)
 */
export const EVENT_ICON_MAPPING: Record<string, string> = {
  LEAK_IMMINENT: 'leak.png',
  STAGNATION: 'stagnation_alert.png',
  CHANGE_NEEDED: 'change_needed.png',
  DIAPER_TOO_OLD: 'too_old_diaper.png',
  OK: 'OK.png',
  HARDWARE_FAULT_CHANGE_DIAPER: 'Hardware_fault_change_diaper.png',
  CLIP_LOW_BATTERY: 'low_battery.png',
  BATTERY_LOW: 'low_battery.png',
  CLIP_SHORTED: 'clip_problem.png',
  CLIP_DETACHED: 'detached.png',
  CLIP_LOST_CONTACT: 'lost_contact.png',
  INVALID_DIAPER: 'unknow_diaper.png',
  CLIP_IDENTIFYING: 'identifying_filter.png',
  POWER_SAVE: 'power_saving.png',
  POLICY_UNMONITORED: 'unmonitored.png',
  CLIENT_MARKED_AWAY: 'unmonitored.png',
  CLIENT_AWAY: 'unmonitored.png',
  CLIENT_RETURNED: 'unmonitored.png',
  WET_DIAPER_REATTACHED: 'change_needed.png',
  WET_DIAPER_ATTACHED: 'change_needed.png',
  CLIP_REBOOTED_RECOVERED: 'OK.png',
  CLIP_REBOOTED: 'clip_problem.png',
  CLIP_REPLACEMENT: 'detached.png',
  SESSION_START: 'OK.png',
  SESSION_END: 'unmonitored.png',
  LOCATION_MISMATCH: 'clip_problem.png',
  RELAY_LOST_CONTACT: 'lost_contact.png',
  RELAY_CONTACT_RESTORED: 'OK.png',
  CLIP_CONTACT_RESTORED: 'OK.png',
};

/**
 * Get event icon URL
 */
export function getEventIcon(eventType: string): string {
  const iconFile = EVENT_ICON_MAPPING[eventType] || 'OK.png';
  return `/icons/${iconFile}`;
}

const IMAGE_API_PATH = '/api/image';

/**
 * `notification.imageUrl` comes from nova-poller: `${IMAGE_GENERATOR_URL}?...`. In
 * docker-compose, `IMAGE_GENERATOR_URL` often uses `http://host.docker.internal:3000`
 * or `http://ops-ui:3000` so other containers can reach ops-ui. The Live Monitor
 * and mock partner view render that URL in {@code <img src>}, so the **browser** must
 * be able to resolve it. Rewrite known Docker-only hostnames to the current page
 * origin (same ops-ui that serves the SPA) while preserving the query string.
 */
export function resolveNotificationImageUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  if (typeof window === 'undefined') return url;
  try {
    const u = new URL(url, window.location.href);
    if (
      (u.pathname === IMAGE_API_PATH || u.pathname.endsWith(IMAGE_API_PATH)) &&
      (u.hostname === 'host.docker.internal' || u.hostname === 'ops-ui')
    ) {
      return `${window.location.origin}${IMAGE_API_PATH}${u.search}`;
    }
  } catch {
    return url;
  }
  return url;
}

